import { useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import {
  Home,
  Trophy,
  Dumbbell,
  Swords,
  User,
  BarChart2,
  AlertTriangle,
  CheckCircle,
  XCircle,
} from 'lucide-react-native';
import { FLAGS } from '@/lib/featureFlags';

const QA_ROUTES: { label: string; path: string; icon: string; note: string }[] = [
  { label: 'Home', path: '/(tabs)/home', icon: 'home', note: 'Baseline checklist + freshness alerts' },
  { label: 'Record (Log)', path: '/(tabs)/record', icon: 'dumbbell', note: 'Log V1 lifts' },
  { label: 'Compete (Duels)', path: '/(tabs)/duels', icon: 'swords', note: 'Create/accept duels' },
  { label: 'Rank (Leaderboard)', path: '/(tabs)/leaderboards', icon: 'trophy', note: 'Global + gym rankings' },
  { label: 'Profile', path: '/(tabs)/profile', icon: 'user', note: 'Settings, PRs, workout history' },
  { label: 'Gym Leaderboard', path: '/gym/leaderboard', icon: 'bar-chart', note: 'Per-gym rankings' },
  { label: 'My PRs', path: '/my-prs', icon: 'trophy', note: 'Personal records' },
];

const KILL_ROUTES: { label: string; path: string; expected: string }[] = [
  { label: 'Feed', path: '/(tabs)/feed', expected: 'Redirect → Home' },
  { label: 'Badges', path: '/(tabs)/badges', expected: 'Redirect → Home' },
  { label: 'Friends', path: '/(tabs)/friends', expected: 'Redirect → Home' },
  { label: 'Map', path: '/(tabs)/map', expected: 'Redirect → Home' },
  { label: 'Body Metrics', path: '/(tabs)/body-metrics', expected: 'Redirect → Home' },
  { label: 'Paywall', path: '/paywall', expected: 'Redirect → Home' },
];

const ICON_MAP: Record<string, React.ComponentType<any>> = {
  home: Home,
  trophy: Trophy,
  dumbbell: Dumbbell,
  swords: Swords,
  user: User,
  'bar-chart': BarChart2,
};

export default function PilotQAScreen() {
  const router = useRouter();
  const { theme } = useTheme();

  useEffect(() => {
    if (!__DEV__) {
      router.replace('/(tabs)/home');
    }
  }, [router]);

  if (!__DEV__) return null;

  return (
    <ScrollView
      style={[styles.container, { backgroundColor: theme.colors.background }]}
      contentContainerStyle={styles.content}>
      <Text style={[styles.title, { color: theme.colors.text }]}>PILOT QA</Text>
      <Text style={[styles.subtitle, { color: theme.colors.textSecondary }]}>
        Quick links to critical V1 routes for smoke testing.
      </Text>

      <TouchableOpacity
        style={[styles.routeCard, { backgroundColor: theme.colors.accent, borderColor: theme.colors.accent }]}
        onPress={() => router.push('/(dev)/dev-tools' as any)}>
        <Text style={[styles.routeLabel, { color: '#fff' }]}>DEV TOOLS HUB</Text>
      </TouchableOpacity>

      <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>V1 Active Routes</Text>
      {QA_ROUTES.map((route) => {
        const Icon = ICON_MAP[route.icon] || Home;
        return (
          <TouchableOpacity
            key={route.path}
            style={[styles.routeCard, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}
            onPress={() => router.push(route.path as any)}>
            <Icon size={20} color={theme.colors.accent} />
            <View style={styles.routeInfo}>
              <Text style={[styles.routeLabel, { color: theme.colors.text }]}>{route.label}</Text>
              <Text style={[styles.routeNote, { color: theme.colors.textSecondary }]}>{route.note}</Text>
            </View>
            <Text style={[styles.routePath, { color: theme.colors.textSecondary }]}>{route.path}</Text>
          </TouchableOpacity>
        );
      })}

      <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
        V1 Kill Routes (Should Redirect)
      </Text>
      <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
        Feature Flags (current state)
      </Text>
      {(Object.entries(FLAGS) as [string, boolean][]).map(([key, val]) => (
        <View
          key={key}
          style={[styles.flagRow, { borderColor: theme.colors.border }]}>
          {val ? (
            <CheckCircle size={16} color="#22c55e" />
          ) : (
            <XCircle size={16} color="#ef4444" />
          )}
          <Text style={[styles.flagKey, { color: theme.colors.text }]}>{key}</Text>
          <Text style={[styles.flagVal, { color: val ? '#22c55e' : '#ef4444' }]}>
            {String(val)}
          </Text>
        </View>
      ))}

      {KILL_ROUTES.map((route) => (
        <TouchableOpacity
          key={route.path}
          style={[styles.routeCard, { backgroundColor: theme.colors.surface, borderColor: theme.colors.border }]}
          onPress={() => router.push(route.path as any)}>
          <AlertTriangle size={20} color="#f59e0b" />
          <View style={styles.routeInfo}>
            <Text style={[styles.routeLabel, { color: theme.colors.text }]}>{route.label}</Text>
            <Text style={[styles.routeNote, { color: '#f59e0b' }]}>{route.expected}</Text>
          </View>
          <Text style={[styles.routePath, { color: theme.colors.textSecondary }]}>{route.path}</Text>
        </TouchableOpacity>
      ))}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  content: { padding: 20, paddingBottom: 60 },
  title: { fontSize: 28, fontWeight: '800', marginBottom: 4 },
  subtitle: { fontSize: 14, marginBottom: 24 },
  sectionTitle: { fontSize: 18, fontWeight: '700', marginTop: 20, marginBottom: 12 },
  routeCard: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 14,
    borderRadius: 2,
    borderWidth: 1,
    marginBottom: 8,
    gap: 12,
  },
  routeInfo: { flex: 1 },
  routeLabel: { fontSize: 15, fontWeight: '600' },
  routeNote: { fontSize: 12, marginTop: 2 },
  routePath: { fontSize: 11, fontFamily: 'monospace' },
  flagRow: {
    flexDirection: 'row' as const,
    alignItems: 'center' as const,
    paddingVertical: 6,
    paddingHorizontal: 12,
    gap: 8,
    borderBottomWidth: 0.5,
  },
  flagKey: { flex: 1, fontSize: 13, fontFamily: 'monospace' },
  flagVal: { fontSize: 13, fontWeight: '600' as const },
});
